import { UAParser } from "ua-parser-js";
import { MediaType } from "./types";

export interface GeolocationCoords {
  latitude: number;
  longitude: number;
}
export interface LocationResult {
  success: boolean;
  code?: string;
  location?: string;
  error?: string;
  coords?: GeolocationCoords;
}

export const getDeviceOS = (): string => {
  const userAgent =
    navigator.userAgent || navigator.vendor || (window as any).opera;

  if (/windows phone/i.test(userAgent)) return "Windows Phone";
  if (/android/i.test(userAgent)) return "Android";
  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream)
    return "iOS";

  switch (true) {
    case /Win/.test(userAgent):
      return "Windows";
    case /Mac/.test(userAgent):
      return "MacOS";
    case /Linux/.test(userAgent):
      return "Linux";
    default:
      return "Unknown OS";
  }
};

export const getPlatformFromUAParser = (): string => {
  const parser = new UAParser();
  return parser.getOS().name || "Unknown Platform";
};

export const getBrowserInfo = (): string => {
  const parser = new UAParser();
  const browser = parser.getBrowser();
  return `${browser.name || "Unknown"} ${browser.version || ""}`;
};

export const fetchDeviceIP = async (): Promise<string> => {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error("Failed to fetch IP:", error);
    return "Unable to fetch IP";
  }
};

export const getGeolocation = (): Promise<{
  latitude: number;
  longitude: number;
}> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject({ code: 0, message: "Geolocation not supported" });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  });
};

export const getReadableLocation = async (): Promise<LocationResult> => {
  try {
    if (!navigator.geolocation) {
      return {
        success: false,
        code: "UNSUPPORTED",
        error: "Your browser does not support location access.",
      };
    }

    const coords = await getGeolocation();

    const response = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${coords.latitude}&longitude=${coords.longitude}&localityLanguage=en`,
    );

    if (!response.ok) {
      return {
        success: false,
        code: "GEOCODE_FAILED",
        error: `Location service error (${response.status}). Please try again.`,
      };
    }

    const data = await response.json();

    const locationParts = [
      data.locality || data.city,
      data.principalSubdivision,
      data.countryName,
    ].filter(Boolean);

    const location =
      locationParts.length > 0
        ? locationParts.join(", ")
        : data.localityInfo?.administrative?.[0]?.name ||
          "Location unavailable";

    return { success: true, location, coords };
  } catch (error: any) {
    const code = error?.code;

    if (code === 1) {
      return {
        success: false,
        code: "PERMISSION_DENIED",
        error:
          "Location permission was denied. Please allow it to verify your email.",
      };
    }
    if (code === 2) {
      return {
        success: false,
        code: "POSITION_UNAVAILABLE",
        error:
          "We couldn’t detect your location. Turn on location services and try again.",
      };
    }
    if (code === 3) {
      return {
        success: false,
        code: "TIMEOUT",
        error: "Location request timed out. Please retry.",
      };
    }

    return {
      success: false,
      code: "UNKNOWN",
      error: error?.message || "An unexpected error occurred.",
    };
  }
};

export const isEmail = (value?: string) => {
  if (!value) return false;

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
};

export function isActivelySubscribed(
  subscriptions: any[],
  creatorUsername: string | undefined,
): { isActive: boolean; subscription: any } {
  // Find the subscription for this creator
  const subscription = subscriptions?.find(
    (sub) => sub?.creator?.username === creatorUsername,
  );

  // If no subscription found, return false
  if (!subscription) {
    return { isActive: false, subscription: null };
  }

  // Check if subscription is still active (end date is in the future)
  const isActive = new Date(subscription?.endDate) > new Date();
  return {
    isActive,
    subscription,
  };
}

export function decodeToken(token: string) {
  if (!token || typeof token !== "string") return null;

  const parts = token.split(".");
  if (parts.length !== 3) {
    console.error("Invalid JWT format");
    return null;
  }

  try {
    const payload = parts[1];
    // JWT uses base64url, not plain base64 — swap chars and pad
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "=",
    );
    const decoded = atob(padded);

    // Handle UTF-8 characters correctly
    const jsonPayload = decodeURIComponent(
      Array.from(decoded)
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join(""),
    );

    return JSON.parse(jsonPayload);
  } catch (err) {
    console.error("Failed to decode token:", err);
    return null;
  }
}
export const formatTimeAgo = (
  dateString: string | null | undefined,
): string => {
  if (!dateString?.trim()) return "-";

  const dateWithTimezone =
    dateString.endsWith("Z") ||
    dateString.includes("+") ||
    (dateString.includes("T") && dateString.split("T")[1]?.includes("-"))
      ? dateString
      : `${dateString}Z`;

  const date = new Date(dateWithTimezone);

  if (Number.isNaN(date.getTime())) return "-";

  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 0) return "just now";
  if (seconds < 10) return "just now";
  if (seconds < 60) return `${seconds}s ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;

  const years = Math.floor(days / 365);
  return `${years}y ago`;
};

export const getMediaType = (files: File[]): MediaType => {
  if (files.length === 0) return "PHOTO";

  const firstFile = files[0];
  const fileType = firstFile.type;

  if (fileType.startsWith("image/")) return "PHOTO";
  if (fileType.startsWith("video/")) return "VIDEO";
  if (fileType.startsWith("audio/")) return "AUDIO";
  return "DOCUMENT";
};

export const getWebSocketUrl = () => {
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_WS_URL || "wss://dev.fanation.app/api/v1/ws";
  }
  return import.meta.env.VITE_WS_URL || "wss://fanation.app/api/v1/ws";
};

/** Referenced by useLiveStream.ts but never shared from the old project —
 *  every call site there already wraps it in its own try/catch, so this
 *  just needs to turn a STOMP frame body into an object or fail loudly
 *  enough for that surrounding catch to log it. Standard JSON.parse is all
 *  the old code's usage implies; swap this for the real one if yours does
 *  anything more (e.g. a specific envelope shape). */
export function parseLiveEvent(raw: string): any {
  return JSON.parse(raw);
}
